/**
 * @Author: zachalam
 * @Date:   2017-01-19T17:15:26-08:00
 * @Last modified by:   hiral
 * @Last modified time: 2024-08-05
 */

import React, { useCallback, useEffect, useState } from 'react';
import Profile from '../components/pages/Profile/Profile';
import {
  refreshExternalProfile,
  updateProfileContent,
  refreshProfile,
  removeExternalProfile,
  updateVideoStatus,
} from '../actions/profileActions';
import { unlockOffer } from '../actions/offerActions';
import { clearIndividualError } from '../actions/commonActions';
import { withPusher } from '../contexts/PusherContext';
import { PUSHER } from '../config/constants';
import { getGlobalNotifications } from '../actions/globalNotificationActions';
import useMixPanel from '../hooks/useMixPanel';
import useUnmount from '../hooks/useUnmount';
import { EVENT_NAMES } from '../constants/mixpanel';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ProfileProvider } from '../contexts/ProfileContext';
import { mixpanelTrackProfileClosed } from '../utils/mixpanel/profileClosed';
import { mixpanelTrackPushNotifClickedAfterRedirection } from '../utils/mixpanel/pushNotifClicked';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import MasterConfig from 'config/Master';

const ProfileContainer = props => {
  const dispatch = useDispatch();
  const { errors } = useSelector((state: RootState) => state.common);
  const myprofile = useSelector((state: RootState) => state.profile);
  const profile = useSelector((state: RootState) => state.extprofile);

  const [, setCurrentProfileHashId] = useState(null);
  const { mixpanelData, resetMixpanelData, trackPackagesPageVisited } = useMixPanel();

  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (params.hashId !== profile.hash_id) {
      dispatch(removeExternalProfile());
    }
  }, [params.hashId]);

  useUnmount(() => {
    if (myprofile?.hash_id !== profile?.hash_id) {
      mixpanelTrackProfileClosed(mixpanelData, resetMixpanelData);
    }
  });

  useEffect(() => {
    if (location) {
      mixpanelTrackPushNotifClickedAfterRedirection(location);
    }
  }, [location]);

  useEffect(() => {
    const { bindListener, stopListening } = props;

    window.scrollTo(0, 0);
    setCurrentProfileHashId(myprofile.hash_id);
    //refreshmyprofile
    dispatch(refreshProfile());

    bindListener && bindListener(listen);

    if (typeof errors === 'object') {
      const errorKeys = Object.keys(errors);

      if (errorKeys.length > 0) {
        errorKeys.map(key => {
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          dispatch(clearIndividualError(key));
        });
      }
    }

    return () => {
      stopListening && stopListening(listen);
      dispatch(getGlobalNotifications());
      resetMixpanelData(EVENT_NAMES.OFFER_MADE);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // redirect to own profile if no hashID present in the url
    const urlPath = location.pathname;
    const noHashIdUrl = urlPath === '/profile' || urlPath === '/profile/';

    if (profile.notFound && myprofile.hash_id !== null && noHashIdUrl) {
      navigate(`/profile/${myprofile.hash_id}`);
      return;
    }
  }, [profile.notFound, myprofile.hash_id]);

  useEffect(() => {
    setTimeout(() => dispatch(refreshExternalProfile(params.hashId)), 0);
    setCurrentProfileHashId(myprofile.hash_id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.hashId]);

  const listen = () => {
    const { onRealtimeEvent } = props;
    const { EVENTS } = PUSHER;

    onRealtimeEvent(EVENTS.ACCOUNT_ALERTS, data => {
      if (MasterConfig.APP_ENV !== 'production') {
        console.log(`Notification Received from Pusher for event - ${EVENTS.ACCOUNT_ALERTS}`, data);
      }

      if (data.type === 'video-event') {
        if (data.data && data.data.video && data.data.video.id && data.data.video.status) {
          dispatch(updateVideoStatus(data.data.video.id, data.data.video.status));
          dispatch(refreshProfile());
        }

        return false;
      }

      if (data.type !== 'offers' || (data.type === 'mail' && data.action !== 'unlock')) {
        return false;
      }

      dispatch(updateProfileContent(params.hashId));
    });
  };

  const unlockMyOffer = (hashId: string, triggerToBuy: boolean, callback = () => undefined) => {
    return dispatch(unlockOffer(hashId, null, triggerToBuy)).then(() => {
      callback();
      navigate(`/mail/${hashId}`);
    });
  };

  const handleUnlockOffer = useCallback((hashId: string) => {
    if (myprofile.credits < profile.offer.data.credit_cost) {
      trackPackagesPageVisited({ Source: 'Unlock Attempt' });
      return navigate('/packages');
    }

    unlockMyOffer(
      profile.offer?.data?.thread_uid ?? hashId,
      mixpanelData[EVENT_NAMES.CONVERSATION_UNLOCKED].triggerToBuy,
      () => {
        resetMixpanelData(EVENT_NAMES.CONVERSATION_UNLOCKED);
      }
    );
  }, []);

  return (
    <ProfileProvider>
      <Profile handleUnlockOffer={handleUnlockOffer} {...props} />
    </ProfileProvider>
  );
};

export default withPusher(ProfileContainer);
